<template>
  <div class="container">
    <div class="row justify-content-center">
      <div class="col col-sm-10 col-md-8 col-lg-7 col-xl-6 col-xxl-6 col-xxxl-6">
        <div class="card border-0 rounded-0 bg-white authentication-card">
          <div class="card-body letter-spacing">
            <h4 class="text-black fw-bold mb-0 text-center">{{ $t('feature.auth.reset_password') }}</h4>
            <p class="mb-0 fs-md-15 fs-lg-16 text-paragraph text-center sub-text" v-html="`${$t('feature.auth.$reset_password.message', {email: form.identifier})}`">
            </p>
            <form v-on:submit="submit">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.confirmation_code.label') }}*
                </label>
                <input type="text" title="Code" class="form-control shadow-none text-black" required v-model="form.code"
                  :class="[{ 'is-invalid': submitted && (errors?.code ?? false) }]"
                  :placeholder="$t('form.confirmation_code.placeholder')" />
                  <div class="form-messages mt-2 px-1" >
                  <p class="small lh-1 m-0 mt-1" v-html="`*${ $t('feature.auth.$reset_password.expires_in', {time: expires_in}) }`">
                    
                  </p>
                  <template v-if="errors?.code ?? false">
                    <p v-for="error in errors.code" :key="error" class="small text-danger lh-1 m-0 mt-1">{{ error }}</p>
                  </template>
                </div>

              </div>
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.password.label') }}
                </label>
                <div class="password-input">
                  <input type="password" title="Password" class="form-control shadow-none text-black" required
                    v-model="form.password" :class="[{ 'is-invalid': submitted && !passwordIsValid }]"
                    :placeholder="$t('form.password.placeholder')" />
                  <i class="ph ph-eye position-absolute top-0 end-0 fs-18 text-muted" v-on:click="togglePassword($event)"></i>
                </div>
                <div class="form-messages mt-2 px-1" v-if="errors?.password ?? false">
                  <p v-for="error in errors.password" :key="error" class="small text-danger lh-1 m-0 mt-1">{{ error }}</p>
                </div>
              </div>
              <div class="form-group mb-15 mb-sm-20">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.password_confirmation.label') }}
                </label>
                <div class="password-input">
                  <input type="password" title="Password" class="form-control shadow-none text-black" required
                    v-model="form.password_confirmation"
                    :class="[{ 'is-invalid': submitted && (!passwordIsValid || !confirmPasswordIsValid) }]"
                    :placeholder="$t('form.password_confirmation.placeholder')" />
                  <i class="ph ph-eye position-absolute top-0 end-0 fs-18 text-muted" v-on:click="togglePassword($event)"></i>
                </div>
              </div>
              <button class="default-btn transition fw-medium fs-md-15 fs-lg-16 d-block w-100" type="submit"
                v-on:click="setPristine" v-if="!loading">
                {{ $t('feature.auth.reset_password') }}
              </button>
              <SpinnerLoading v-else/>
              <div class="text-center mt-15 mt-sm-20 mt-md-25">
                <router-link to="/login"
                  class="link-btn text-decoration-none text-primary fw-medium fs-md-15 fs-lg-16 position-relative">
                  <i class="ph-bold ph-arrow-left"></i>
                  {{ $t('cta.back_to_login') }}
                </router-link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    
  </div>
  <AlertModal id="alertModal" @confirm="resendRequest()" :title="$t('feature.auth.$reset_password.code_expired')" :message="$t('feature.auth.$reset_password.code_expired_message')"></AlertModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import API from '../../../services/api/api.service';
import { Alert, Modal } from "bootstrap";
import { ResetPasswordAuthFactor, ResetPassword } from '@/utils/models/user.model';
import { AxiosError } from 'axios';
import AlertModal from "@/components/Widgets/AlertModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { toggle_password_visibility } from "@/utils/methods/form_utils";

export default defineComponent({
  name: "ResetPassword",
  data: function () {
    return {
      loading: false,
      form: {
        scope: ResetPasswordAuthFactor.email,
        identifier: this.$route.query.identifier
      } as ResetPassword,
      submitted: false,
      errorMessage: undefined as Alert | undefined,
      errors: undefined as any,
      timer: undefined as number | undefined,
      alertModal: null as Modal | null,
      expires_in: parseInt(this.$route.query.expires_in as string ?? '120'),
    }
  },
  mounted() {
    this.errorMessage = new Alert('#errorMessage');
    this.timer = setInterval(this.countdown, 1000);
    this.alertModal = new Modal('#alertModal', {});
  },
  computed: {
    passwordIsValid(): boolean {
      return this.form.password?.length != 0 && !(this.errors?.password ?? false);
    },
    confirmPasswordIsValid(): boolean {
      return this.form?.password_confirmation?.length != 0 && this.form?.password === this.form.password_confirmation;
    }
  },
  methods: {
    submit(e) {
      this.submitted = true;
      this.errors = undefined;
      e?.preventDefault();
      if (this.passwordIsValid && this.confirmPasswordIsValid) {
        console.log(this.form);
        this.sendRequest();
      }
    },
    sendRequest() {
      this.loading = true;
      API.resetPassword(this.form).then((response) => {
        if (response.data.success === true) {
          this.$router.push('/login');
        } else {
          this.showError();
        }
      }).catch((error: AxiosError<any>) => {
        this.errors = error.response?.data?.errors;
        this.showError();
      }).finally(() => {
        this.loading = false;
      });
    },
    setPristine() {
      this.submitted = true;
    },
    showError() {
      const error = document.getElementById('errorMessage');
      error?.classList.add('show');
      setTimeout(() => {
        this.hideError();
      }, 5000)
    },
    hideError() {
      const error = document.getElementById('errorMessage');
      error?.classList.remove('show');
    },
    countdown() {
      this.expires_in --;
      if(this.expires_in === 0) {
        this.stopTimer(); 
        this.alertModal?.show();
        setTimeout(this.resendRequest, 5000)
      }
    },
    resendRequest() {
      this.$router.push({path: '/forgot-password', query: {identifier: this.form.identifier}})
      this.alertModal?.hide();
    },
    stopTimer() {
      console.log('STOP_TIMER');
      clearInterval(this.timer);
    },
    togglePassword(event?: Event) {
      toggle_password_visibility(event);
    }
  },
  deactivated() {
    this.stopTimer();
  },
  unmounted() {
    this.stopTimer();
  },
  components: {
    AlertModal,
    SpinnerLoading
  },
});
</script>